import { useNavigate } from '@tanstack/react-router';
import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@/auth/auth-provider';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useRuntimeConfig } from '../runtime-config-context';
import { visibleNav } from '../nav';
import { useTheme } from '../theme/theme-provider';
import { navLinkProps } from './nav-link';

interface PaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}
const PaletteContext = createContext<PaletteContextValue | null>(null);

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const value = useMemo(() => ({ open, setOpen }), [open]);
  return <PaletteContext value={value}>{children}</PaletteContext>;
}

export function useCommandPalette(): PaletteContextValue {
  const ctx = use(PaletteContext);
  if (!ctx) throw new Error('useCommandPalette must be used inside <CommandPaletteProvider>');
  return ctx;
}

/** ⌘K: jump anywhere you have access to. Entity search (/api/search) joins in Phase 4. */
export function CommandPalette() {
  const { open, setOpen } = useCommandPalette();
  const navigate = useNavigate();
  const { can, superAdmin, logout } = useAuth();
  const { setMode } = useTheme();
  const { presentation } = useRuntimeConfig();
  const sections = visibleNav(presentation.navigation.items, { can, superAdmin });

  const run = useCallback(
    (fn: () => void) => {
      setOpen(false);
      fn();
    },
    [setOpen],
  );

  return (
    // Composed here rather than with shadcn's CommandDialog, which renders its hidden title outside
    // the dialog, leaving a stray "Search" heading on every page.
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="top-1/3 translate-y-0 overflow-hidden rounded-xl! p-0" showCloseButton={false}>
        <DialogHeader className="sr-only">
          <DialogTitle>Search</DialogTitle>
          <DialogDescription>Jump to a page or run an action</DialogDescription>
        </DialogHeader>
        <Command>
          <CommandInput placeholder="Where to?" />
          <CommandList>
            <CommandEmpty>Nothing matches.</CommandEmpty>
            {sections.map((section) => (
              <CommandGroup key={section.id} heading={section.label}>
                {section.items
                  .flatMap((item) => [
                    { item, label: item.label },
                    ...(item.children ?? []).map((child) => ({ item: child, label: `${item.label} › ${child.label}` })),
                  ])
                  .map(({ item, label }) => (
                    <CommandItem
                      key={item.id}
                      value={`${section.label} ${label}`}
                      onSelect={() => run(() => void navigate(navLinkProps(item)))}
                    >
                      {item.icon ? <item.icon aria-hidden /> : <span aria-hidden className="size-4" />}
                      {label}
                    </CommandItem>
                  ))}
              </CommandGroup>
            ))}
            <CommandSeparator />
            <CommandGroup heading="Actions">
              <CommandItem onSelect={() => run(() => setMode('light'))}>Theme: light</CommandItem>
              <CommandItem onSelect={() => run(() => setMode('dark'))}>Theme: dark</CommandItem>
              <CommandItem onSelect={() => run(() => setMode('system'))}>Theme: follow system</CommandItem>
              {/* Opens the System page's confirmation (`clear-cache` is also a brand-overlay id). */}
              {can('VIEW_SINGLE_OPERATORS', 'UPDATE_OPERATORS') && (
                <CommandItem
                  value="Clear cache"
                  onSelect={() => run(() => void navigate({ to: '/system', search: { confirm: 'clear-cache' } }))}
                >
                  Clear cache…
                </CommandItem>
              )}
              <CommandItem onSelect={() => run(() => void logout())}>Sign out</CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
